import { onMount, option, select as selectEl, type Mountable } from '@llui/dom';

/**
 * `<SelectInput>` — styled native `<select>` with consistent sizing.
 *
 * Wraps `@llui/dom`'s `select` element helper with a token-correct
 * visual treatment that matches the rest of the design-system input
 * chrome (surface-base background, border-default, radius-md, focus
 * ring on border-strong). Native semantics preserved so keyboard /
 * screen-reader / mobile-native-picker behavior is free.
 *
 * Sizing variants:
 *
 *   - `'auto'`  — width follows content (default for calendar pickers,
 *                 toolbar dropdowns; matches `<input>` only when given
 *                 an explicit max-inline-size).
 *   - `'sm'`    — 7rem max (era / mode toggles)
 *   - `'md'`    — 11rem max (month names, mid-length enums)
 *   - `'lg'`    — 18rem max (long enum values, location refs)
 *   - `'full'`  — fills the parent (form rows)
 *
 * Construction:
 *
 *   selectInput({
 *     value: parts.era,
 *     options: eras.map((e) => ({ value: e.id, label: e.label })),
 *     onChange: (v) => handlePart({ era: v }),
 *     ariaLabel: t('calendar.era'),
 *     size: 'md',
 *   })
 */

export interface SelectInputOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Optional `data-*` attrs forwarded to the <option>. */
  dataAttrs?: Record<string, string>;
}

export type SelectInputSize = 'auto' | 'sm' | 'md' | 'lg' | 'full';

export interface SelectInputOptions {
  value: string;
  options: SelectInputOption[];
  onChange: (value: string) => void;
  /** Stable HTML id (used by the host's <label for=>). */
  id?: string;
  /** Native form name. */
  name?: string;
  ariaLabel?: string;
  /** Id of a describing element, mirrored to `aria-describedby`. */
  ariaDescribedby?: string;
  testid?: string;
  size?: SelectInputSize;
  disabled?: boolean;
  className?: string;
}

let selectInputSeq = 0;

export function selectInput(opts: SelectInputOptions): Mountable {
  const size: SelectInputSize = opts.size ?? 'auto';
  const klass = `dl-select dl-select--${size}${opts.className ? ` ${opts.className}` : ''}`;
  // A stable per-instance marker so the onMount reflector can locate THIS
  // select inside the component's build root (onMount's `root` is the build
  // parent, not the element itself).
  const reflectId = `dl-sel-${(selectInputSeq += 1)}`;
  return selectEl(
    {
      class: klass,
      value: opts.value,
      'data-dl-select': reflectId,
      ...(opts.id !== undefined ? { id: opts.id } : {}),
      ...(opts.name !== undefined ? { name: opts.name } : {}),
      ...(opts.ariaLabel !== undefined ? { 'aria-label': opts.ariaLabel } : {}),
      ...(opts.ariaDescribedby !== undefined
        ? { 'aria-describedby': opts.ariaDescribedby }
        : {}),
      ...(opts.testid !== undefined ? { 'data-testid': opts.testid } : {}),
      ...(opts.disabled === true ? { disabled: true } : {}),
      onChange: (event: Event) => {
        const next = (event.target as HTMLSelectElement).value;
        opts.onChange(next);
      },
    },
    [
      ...opts.options.map((o) =>
        option(
          {
            value: o.value,
            ...(o.disabled === true ? { disabled: true } : {}),
            ...(o.dataAttrs ?? {}),
          },
          [o.label],
        ),
      ),
      // Reflect the controlled value imperatively after the options mount. The
      // signal runtime applies neither a parent <select> `value` prop nor a
      // per-option `selected` flag reliably to map()-generated <option>s at
      // mount, so set the native `HTMLSelectElement.value` directly once the
      // options are in the DOM. The host re-mounts the select when its
      // controlled value changes, so this fires again on every value change.
      onMount((root) => {
        const el = (root as Element).querySelector?.(
          `[data-dl-select="${reflectId}"]`,
        ) as HTMLSelectElement | null;
        if (el) el.value = opts.value;
      }),
    ],
  );
}
